import React, { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import toast from 'react-hot-toast';

import Yup from '../../../../utils/yupExtensions';
import styles from './EmployerPortalJobDetail.module.css';
import PerformanceFilter from './PerformanceFilter';
import { Typography } from '../../../Typography/typography';
import { jobDetailUI } from './JobDetailUI';
import { bestMatchUI } from './BestMatchUI';
import { applicationUI } from './ApplicationUI';
import { BackToJobIcon } from '../../../../assets/images/icons';
import { jobDetailHeader } from './JobDetailHeader';
import { getJobApplicationsList, updateApplicationField, updateJobField } from '../../../../services/jobService';
import { useGetJobCategoriesHook, useGetSkillsHook } from '../../../../utils/utilityHooks';
import FormSelectField from '../../UpdatedFormUI/FormSelectField/FormSelectField';
// import { FormSelectField } from '../../../UI/UpdatedFormUI/FormSelectField/FormSelectField';

function EmployerPortalJobDetail({ jobDetail, jobs, setSelectedJobsForDetail, setJobs, similarJobs }) {
    const { t } = useTranslation();
    const [showFullDescription, setShowFullDescription] = useState(false);
    const [activeButton, setActiveButton] = useState('Daily');

    let skills = useGetSkillsHook();
    const [page, setPage] = useState(1);
    const [skillsData, setSkillsData] = useState([]);
    const [applications, setApplications] = useState([]);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [canNotDeletePopup, setcanNotDeletePopup] = useState(false);

    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);

    const navigate = useNavigate();
    const categoryOptions = useGetJobCategoriesHook();
    const handleGoBack = () => {
        setSelectedJobsForDetail(false)
        navigate('/employer-portal/jobs');
    };
    const jobCategory = categoryOptions.filter((item) => item.value == jobDetail.category)[0]?.label;

    const today = dayjs();
    const last7Days = Array.from({ length: 7 }, (_, i) => today.subtract(i, 'day').format('YYYY-MM-DD')).reverse();

    const statusOptions = [
        { value: 'active', label: t('filterButtons.open') },
        { value: 'paused', label: t('general.UI.paused') },
        { value: 'closed', label: t('general.UI.closed') },
        { value: 'expired', label: t('forms.welcome_job_seeker.step_1.expired') }
    ];

    // Check if job status is draft
    const isDraftJob = jobDetail.status === 'draft';

    const handleStatusChange = (status) => {
        updateJobField({
            job_id: jobDetail.id,
            token: userToken,
            field: 'status',
            value: status
        })
        .then((response) => {
            setJobs(prevState => ({
                ...prevState,
                jobs: prevState.jobs.map(job => 
                    job.id === jobDetail.id ? {...job, status: status} : job
                )
            }));
            jobDetail.status = status;
            
            // Show success toast based on the status
            if (status === 'active') {
                toast.success(t('filterButtons.open') + ' - ' + 'Status updated successfully');
            } else if (status === 'paused') {
                toast.success(t('general.UI.paused') + ' - ' + 'Status updated successfully');
            } else if (status === 'closed') {
                toast.success(t('general.UI.closed') + ' - ' + 'Status updated successfully');
            } else if (status === 'expired') {
                toast.success(t('forms.welcome_job_seeker.step_1.expired') + ' - ' + 'Status updated successfully');
            } else {
                toast.success('Status updated successfully');
            }
        })
        .catch((error) => {
            console.log(error);
            // Show error toast
            if (error?.response?.data?.error) {
                toast.error(error.response.data.error);
            } else {
                toast.error(t('general.UI.something_went_wrong'));
            }
        });
    };

    useEffect(() => {
        if (Object.keys(jobDetail)?.length > 0) {
            const skillsDataArrray = [];
            jobDetail?.job_skills?.map((e) => {
                const filterData = skills.filter((item) => item.id == e.job_id);
                skillsDataArrray.push(filterData[0]?.label);
            });
            if (skillsDataArrray?.length > 0) {
                setSkillsData(skillsDataArrray);
            }
        }
    }, [jobDetail, skills]);

    useEffect(() => {
        const filters = {
            show_new: false,
            show_shortlisted: false,
            show_reviewing: false,
            show_interviewing: false,
            show_hired: false,
            show_rejected: false,
            show_undecided: false,
        };
        getJobApplicationsList(jobDetail.id, userToken, page, 1000, { filters })
            .then(response => { setApplications(response); })
            .catch(error => { console.log(error); });
    }, [jobDetail, user, userToken, page]);
    useEffect(() => {
        jobDetail.allNewApplications.map((item) => {
            updateApplicationField({
                application_id: item.id,
                token: userToken,
                field: 'status',
                value: `Reviewing`
            })
        })
    }, [jobDetail])
    const aggregateData = (data, type, isApplication) => {
        if (isApplication) {
            // Handle application data
            if (!data || data?.length === 0) return [];

            if (type === 'Daily') {
                const dataMap = data.reduce((acc, entry) => {
                    const normalizedDate = dayjs(entry.application?.createdAt).format('YYYY-MM-DD');
                    acc[normalizedDate] = (acc[normalizedDate] || 0) + 1; // Increment count for each application on the same date
                    return acc;
                }, {});

                return last7Days.map(day => dataMap[day] || 0);
            } else if (type === 'Weekly') {
                let weeklyData = {};
                data.forEach(entry => {
                    const normalizedDate = dayjs(entry.application?.createdAt).format('YYYY-MM-DD');
                    const weekStart = dayjs(normalizedDate).startOf('week').format('YYYY-MM-DD');
                    weeklyData[weekStart] = (weeklyData[weekStart] || 0) + 1; // Increment count for each application in the same week
                });

                const last4Weeks = Array.from({ length: 4 }, (_, i) => today.subtract(i, 'week').startOf('week').format('YYYY-MM-DD')).reverse();
                return last4Weeks.map(week => weeklyData[week] || 0);
            } else if (type === 'Monthly') {
                let monthlyData = {};
                data.forEach(entry => {
                    const normalizedDate = dayjs(entry.application?.createdAt).format('YYYY-MM-DD');
                    const monthStart = dayjs(normalizedDate).startOf('month').format('YYYY-MM');
                    monthlyData[monthStart] = (monthlyData[monthStart] || 0) + 1; // Increment count for each application in the same month
                });

                const last6Months = Array.from({ length: 6 }, (_, i) => today.subtract(i, 'month').startOf('month').format('YYYY-MM')).reverse();
                return last6Months.map(month => monthlyData[month] || 0);
            }
        } else {
            // Handle views and impressions data
            if (!data || data?.length === 0) return [];

            if (type === 'Daily') {
                const dataMap = data.reduce((acc, entry) => {
                    const normalizedDate = dayjs(entry.date).format('YYYY-MM-DD');
                    acc[normalizedDate] = (acc[normalizedDate] || 0) + entry.count; // Sum counts for the same date
                    return acc;
                }, {});

                return last7Days.map(day => dataMap[day] || 0);
            } else if (type === 'Weekly') {
                let weeklyData = {};
                data.forEach(entry => {
                    const normalizedDate = dayjs(entry.date).format('YYYY-MM-DD');
                    const weekStart = dayjs(normalizedDate).startOf('week').format('YYYY-MM-DD');
                    weeklyData[weekStart] = (weeklyData[weekStart] || 0) + entry.count; // Sum counts for the same week
                });

                const last4Weeks = Array.from({ length: 4 }, (_, i) => today.subtract(i, 'week').startOf('week').format('YYYY-MM-DD')).reverse();
                return last4Weeks.map(week => weeklyData[week] || 0);
            } else if (type === 'Monthly') {
                let monthlyData = {};
                data.forEach(entry => {
                    const normalizedDate = dayjs(entry.date).format('YYYY-MM-DD');
                    const monthStart = dayjs(normalizedDate).startOf('month').format('YYYY-MM');
                    monthlyData[monthStart] = (monthlyData[monthStart] || 0) + entry.count; // Sum counts for the same month
                });

                const last6Months = Array.from({ length: 6 }, (_, i) => today.subtract(i, 'month').startOf('month').format('YYYY-MM')).reverse();
                return last6Months.map(month => monthlyData[month] || 0);
            }
        }
        return [];
    };
    const getXAxisLabels = (type) => {
        if (type === 'Daily') {
            // For Daily view, show short day names (e.g., Mon, Tue, etc.)
            return last7Days.map(day => dayjs(day).format('ddd'));
        } else if (type === 'Weekly') {
            // For Weekly view, show "This Week", "Week 1", "Week 2", etc.
            const last4Weeks = Array.from({ length: 4 }, (_, i) => today.subtract(i, 'week').startOf('week').format('YYYY-MM-DD')).reverse();
            return last4Weeks.map((week, index) => {
                if (index === 0) return 'This Week'; // Current week
                return `Week ${index}`; // Previous weeks
            }).reverse();
        } else if (type === 'Monthly') {
            // For Monthly view, show short month names (e.g., Jan, Feb, etc.)
            const last6Months = Array.from({ length: 6 }, (_, i) => today.subtract(i, 'month').startOf('month').format('YYYY-MM')).reverse();
            return last6Months.map(month => dayjs(month).format('MMM'));
        }
        return [];
    };

    const viewsData = aggregateData(jobDetail?.views_history, activeButton);
    const applicationData = aggregateData(applications?.applications, activeButton, true);
    const impressionsData = aggregateData(jobDetail?.impressions_history, activeButton);
    const formik = useFormik({
        initialValues: { 
            engagementFilter: 'Views',
            status: jobDetail?.status || 'active'
        },
        validationSchema: Yup.object({}),
        onSubmit: async (values) => { },
    });

    return (
        <>
            <div className={styles.backToJobsContainer} >
                <div className={styles.leftSection}>
                    <img src={BackToJobIcon} className={styles.backToJobsIcon} onClick={handleGoBack} />
                    <Typography
                        onClick={handleGoBack}
                        title={t('employerPortalJobDetail.back_to_jobs')}
                        weight={'600'}
                        fontSize={16}
                        color={`#0464B2`}
                        textTransform={'capitalize'} />
                </div>
                {/* Only show status dropdown if job is not in draft status */}
                {!isDraftJob && (
                    <div className={styles.statusDropdownContainer}>
                        <FormSelectField
                            name="status"
                            type="default"
                            hasBorder
                            themeLight
                            options={statusOptions}
                            placeholder={t('general.UI.change_status')}
                            value={formik.values.status}
                            onChange={(status) => {
                                formik.setFieldValue('status', status);
                                handleStatusChange(status);
                            }}
                        />
                    </div>
                )}
            </div>
            <div className={styles.container}>
                <div className={styles.contentContainer}>
                    <div className={styles.jobDetailContainer}>
                        {jobDetailHeader(jobDetail)}
                        {jobDetailUI({ ...jobDetail, jobCategory, skillsData }, setShowFullDescription, showFullDescription)}
                        <div className={`${styles.monitorNumbersWrapper}`}>
                            <Typography title={t('employerPortalJobDetail.performance')} weight={'600'} fontSize={16} />
                            <div className={styles.monitorNumbersContainer}>
                                <div className={styles.monitorNumbers}>
                                    <Typography title={jobDetail.job_views} weight={'700'} fontSize={20} />
                                    <Typography title={t('employerPortalJobDetail.views')} weight={`400`} color={"#414445"} fontSize={16} />
                                </div>
                                <div className={styles.monitorNumbers}>
                                    <Typography title={jobDetail.impressions} weight={'700'} fontSize={20} />
                                    <Typography title={t('employerPortalJobDetail.impressions')} weight={`400`} color={"#414445"} fontSize={16} />
                                </div>
                                <div className={styles.monitorNumbers}>
                                    <Typography title={jobDetail?.applications_count} weight={'700'} fontSize={20} />
                                    <Typography title={t('employerPortalJobDetail.applications')} weight={`400`} color={"#414445"} fontSize={16} />
                                </div>
                            </div>
                            <Typography title={t('employerPortalJobDetail.engagement_metrics')} weight={'600'} fontSize={16} />
                            {PerformanceFilter(activeButton, setActiveButton, formik)}
                            <div style={{ flex: 1, position: "relative", }}>
                                <>
                                    <span style={{ position: "relative", backgroundColor: "green" }}>
                                        <SparkLineChart
                                            showTooltip
                                            showHighlight
                                            data={formik.values.engagementFilter == 'Applications' ? applicationData : formik.values.engagementFilter == 'Views' ? viewsData : impressionsData}
                                            height={100}
                                            colors={['#0464B2']}
                                        />
                                        <div style={{
                                            position: "absolute",
                                            top: "0px",
                                            height: "100%", width: "0.5px",
                                            display: 'flex', borderTop: "0.5px solid #A4AAAC", justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', zIndex: 9,
                                            background: "#A4AAAC"
                                        }} />
                                    </span>
                                    <div style={{ display: 'flex', borderTop: "0.5px solid #A4AAAC", justifyContent: 'space-between', marginTop: '10px', paddingTop: '10px', }}>
                                        {getXAxisLabels(activeButton).map((label, index) => (
                                            <Typography key={index} title={label} weight={'400'} fontSize={12} />
                                        ))}
                                    </div>
                                </>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.sideBarContainer}>
                    {applicationUI(applications, navigate, jobDetail, userToken, handleGoBack, setJobs, showDeleteConfirmation, setShowDeleteConfirmation, canNotDeletePopup, setcanNotDeletePopup)}
                    {similarJobs?.length > 0 && bestMatchUI(similarJobs, navigate)}
                </div>
            </div>
        </>
    );
}

export default EmployerPortalJobDetail;